import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Mail, Smartphone, Monitor, Settings, Save, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import NotificationScopeModal from './NotificationScopeModal';

const NotificationSubscriptions = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Scoping options
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(null);

  useEffect(() => {
    fetchEventTypes();
    fetchSubscriptions();
    fetchStudiesAndSites();
  }, []);

  const fetchStudiesAndSites = async () => {
    try {
      const [studiesRes, sitesRes] = await Promise.all([
        api.get('/api/studies/'),
        api.get('/api/sites/')
      ]);
      
      setStudies(studiesRes.data.results || studiesRes.data || []);
      setSites(sitesRes.data.results || sitesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch studies/sites:', error);
      // Don't show error toast - this is optional functionality
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await api.get('/api/notification-types/');
      // Handle both direct array and paginated response
      const data = response.data.results || response.data;
      // Filter out adverse event notifications (not implemented yet)
      const filteredData = (Array.isArray(data) ? data : []).filter(
        eventType => !eventType.key.startsWith('adverse_event')
      );
      console.log('[NOTIF PREFS] Fetched event types:', filteredData);
      setEventTypes(filteredData);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
      toast.error('Failed to load notification types');
      setEventTypes([]); // Ensure it's always an array
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/api/subscriptions/');
      // Handle both direct array and paginated response
      const data = response.data.results || response.data;
      const subscriptionsArray = Array.isArray(data) ? data : [];
      console.log('[NOTIF PREFS] Fetched subscriptions:', subscriptionsArray);
      setSubscriptions(subscriptionsArray);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load your subscriptions');
      setSubscriptions([]); // Ensure it's always an array
      setLoading(false);
    }
  };

  const getSubscription = (eventTypeId) => {
    return subscriptions.find(sub => sub.event_type === eventTypeId);
  };

  const updateSubscription = async (eventTypeId, channel, enabled) => {
    try {
      setSaving(true);
      const existingSubscription = getSubscription(eventTypeId);
      
      console.log('[NOTIF PREFS] Updating subscription:', {
        eventTypeId,
        channel,
        enabled,
        existingSubscription
      });
      
      // Build the payload with the channel-specific enabled flags
      const email_enabled = channel === 'email' ? enabled : (existingSubscription?.email_enabled || false);
      const in_app_enabled = channel === 'in_app' ? enabled : (existingSubscription?.in_app_enabled || false);
      
      const payload = {
        enabled: email_enabled || in_app_enabled, // Master switch - enabled if any channel is enabled
        email_enabled,
        in_app_enabled,
      };
      
      console.log('[NOTIF PREFS] Payload:', payload);
      
      if (existingSubscription) {
        // Update existing subscription
        console.log('[NOTIF PREFS] Updating existing subscription:', existingSubscription.id);
        await api.patch(`/api/subscriptions/${existingSubscription.id}/`, payload);
      } else {
        // Create new subscription
        console.log('[NOTIF PREFS] Creating new subscription');
        await api.post('/api/subscriptions/', {
          event_type: eventTypeId,
          ...payload
        });
      }
      
      // Refresh subscriptions
      await fetchSubscriptions();
      toast.success('Subscription updated');
    } catch (error) {
      console.error('[NOTIF PREFS] Failed to update subscription:', error);
      console.error('[NOTIF PREFS] Error response:', error.response?.data);
      toast.error('Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const getEventTypeIcon = (eventKey) => {
    if (eventKey.startsWith('survey.')) return '📋';
    if (eventKey.startsWith('participant.')) return '👤';
    if (eventKey.startsWith('dcq.')) return '📝';
    if (eventKey.startsWith('study.')) return '🔬';
    if (eventKey.startsWith('site.')) return '🏥';
    return '📢';
  };

  const getDisplayName = (eventKey) => {
    const displayNames = {
      // Survey notifications
      'survey.missed': 'Missed Surveys',
      'survey.completed': 'Survey Completions', 
      'survey.window_opening': 'Survey Reminders',
      'survey.window_incomplete': 'Incomplete Surveys',
      
      // Participant notifications
      'participant.created': 'New Participants',
      'participant.activated': 'Participant Activations',
      'participant.deactivated': 'Participant Deactivations',
      'participant.updated': 'Participant Updates',
      
      // DCQ notifications
      'dcq.created': 'New Data Queries',
      'dcq.updated': 'Data Query Updates',
      'dcq.status_changed': 'Query Status Changes',
      
      // Study notifications
      'study.created': 'New Studies',
      'study.activated': 'Study Activations',
      'study.updated': 'Study Updates',
      
      // Site notifications
      'site.created': 'New Sites',
      'site.activated': 'Site Activations',
      'site.deactivated': 'Site Deactivations',
      
      // Assessment notifications
      'assessment.assigned': 'Assessment Assignments',
      'assessment.completed': 'Assessment Completions'
    };
    
    return displayNames[eventKey] || eventKey.replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDisplayDescription = (eventKey) => {
    const descriptions = {
      // Survey notifications
      'survey.missed': 'Get alerts when participants miss their survey deadlines',
      'survey.completed': 'Be notified when participants complete their surveys', 
      'survey.window_opening': 'Receive reminders when surveys become available',
      'survey.window_incomplete': 'Alerts for surveys not completed within the window',
      
      // Participant notifications
      'participant.created': 'Know when new participants are enrolled in studies',
      'participant.activated': 'Be informed when participants are activated',
      'participant.deactivated': 'Get notified when participants are deactivated',
      'participant.updated': 'Receive updates when participant information changes',
      
      // DCQ notifications
      'dcq.created': 'Be alerted when new data clarification queries are raised',
      'dcq.updated': 'Get updates when data queries are modified or resolved',
      'dcq.status_changed': 'Know when query statuses change (pending, resolved, etc.)',
      
      // Study notifications
      'study.created': 'Be informed when new studies are created',
      'study.activated': 'Get notified when studies become active',
      'study.updated': 'Receive updates when study information changes',
      
      // Site notifications
      'site.created': 'Know when new sites are added to studies',
      'site.activated': 'Be informed when sites are activated for enrollment',
      'site.deactivated': 'Get alerts when sites are deactivated',
      
      // Assessment notifications
      'assessment.assigned': 'Be notified when assessments are assigned to participants',
      'assessment.completed': 'Know when participants complete their assessments'
    };
    
    return descriptions[eventKey] || eventKey.replace(/[._]/g, ' ');
  };

  const handleScopedSubscription = async (scopeConfig) => {
    try {
      setSaving(true);
      
      // Create scoped subscription
      await api.post('/api/subscriptions/', {
        event_type: selectedEventType.id,
        enabled: true,
        in_app_enabled: true, // Default to in-app for scoped notifications
        email_enabled: false,
        study: scopeConfig.study || null,
        site: scopeConfig.site || null,
        participant_id: scopeConfig.participant_id || null,
      });
      
      // Refresh subscriptions
      fetchSubscriptions();
      toast.success('Advanced notification settings saved');
    } catch (error) {
      console.error('Failed to save scoped subscription:', error);
      toast.error('Failed to save advanced settings');
    } finally {
      setSaving(false);
    }
  };

  const getEventTypePriority = (eventKey) => {
    // High priority events (most users want these)
    const highPriority = ['survey.missed', 'participant.created', 'dcq.created'];
    if (highPriority.includes(eventKey)) return 'high';
    
    // Medium priority events
    const mediumPriority = ['survey.completed', 'participant.activated', 'dcq.updated'];  
    if (mediumPriority.includes(eventKey)) return 'medium';
    
    return 'low';
  };

  const channelOptions = [
    { key: 'in_app', label: 'In-App', icon: Monitor, description: 'Show in notification bell' },
    { key: 'email', label: 'Email', icon: Mail, description: 'Send email notifications' },
    { key: 'sms', label: 'SMS', icon: Smartphone, description: 'Text message alerts' }
  ];

  const groupedEventTypes = (eventTypes || []).reduce((groups, eventType) => {
    const category = eventType.key.split('.')[0]; // survey, participant, dcq, etc.
    if (!groups[category]) groups[category] = [];
    groups[category].push(eventType);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="animate-spin h-8 w-8 text-primary-600 mr-3" />
        <span className="text-gray-600">Loading notification preferences...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Bell className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Notification Preferences</h2>
              <p className="text-gray-600 mt-1">Choose which notifications you want to receive and how you want to receive them.</p>
            </div>
          </div>
        </div>

        {/* Subscription Categories */}
        <div className="p-6 space-y-8">
          {Object.keys(groupedEventTypes).length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-lg mb-2">📭</div>
              <p className="text-gray-600">No notification types available</p>
              <p className="text-sm text-gray-500 mt-1">Check back later or contact support if this persists.</p>
            </div>
          ) : (
            Object.entries(groupedEventTypes).map(([category, events]) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-gray-900 capitalize flex items-center">
                <span className="text-2xl mr-2">
                  {category === 'survey' && '📋'}
                  {category === 'participant' && '👤'}
                  {category === 'dcq' && '📝'}
                  {category === 'study' && '🔬'}
                  {category === 'site' && '🏥'}
                </span>
                {category} Notifications
              </h3>

              <div className="space-y-3">
                {events
                  .sort((a, b) => {
                    const priorityOrder = { high: 0, medium: 1, low: 2 };
                    return priorityOrder[getEventTypePriority(a.key)] - priorityOrder[getEventTypePriority(b.key)];
                  })
                  .map((eventType) => {
                    const subscription = getSubscription(eventType.id);
                    const priority = getEventTypePriority(eventType.key);
                    
                    return (
                      <div
                        key={eventType.id}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          priority === 'high' 
                            ? 'border-orange-200 bg-orange-50' 
                            : priority === 'medium'
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                        }`}
                      >
                        {/* Event Type Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="text-lg mr-2">{getEventTypeIcon(eventType.key)}</span>
                              <h4 className="font-semibold text-gray-900">{getDisplayName(eventType.key)}</h4>
                              {priority === 'high' && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{getDisplayDescription(eventType.key)}</p>
                            {eventType.audience_hint && (
                              <p className="text-xs text-gray-500 mt-1">
                                Typically for: {eventType.audience_hint.replace('_', ' ')}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Channel Toggles & Advanced Settings */}
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-3">
                            {channelOptions.map((channel) => {
                              // Check the channel-specific enabled flag
                              const isEnabled = subscription?.enabled && (
                                channel.key === 'email' ? subscription?.email_enabled :
                                channel.key === 'in_app' ? subscription?.in_app_enabled :
                                channel.key === 'sms' ? subscription?.sms_enabled :
                                false
                              );
                              
                              return (
                                <motion.button
                                  key={`${eventType.id}-${channel.key}`}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => updateSubscription(eventType.id, channel.key, !isEnabled)}
                                  disabled={saving}
                                  className={`flex items-center px-3 py-2 rounded-lg border transition-all duration-200 ${
                                    isEnabled
                                      ? 'bg-primary-100 border-primary-300 text-primary-800'
                                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-300'
                                  } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                  <channel.icon className="h-4 w-4 mr-2" />
                                  <span className="text-sm font-medium">{channel.label}</span>
                                  {isEnabled && <span className="ml-2 text-xs">✓</span>}
                                </motion.button>
                              );
                            })}
                          </div>
                          
                          {/* Advanced Scoping */}
                          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => {
                                setSelectedEventType(eventType);
                                setShowScopeModal(true);
                              }}
                              className="flex items-center text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Advanced Settings (Scope to specific studies, sites, etc.)
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </motion.div>
          ))
          )}
        </div>

        {/* Help Section */}
        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">💡 How it works</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>Survey Missed:</strong> Alert when participants don't complete their surveys (for study staff)</li>
            <li>• <strong>Survey Window Opening:</strong> Reminder when a survey becomes available (for participants)</li>
            <li>• <strong>Survey Completed:</strong> Confirmation when participants finish surveys</li>
            <li>• <strong>Participant Created:</strong> Know when new participants are enrolled</li>
            <li>• <strong>DCQ Created:</strong> Stay informed about new data clarification queries</li>
          </ul>
          <div className="mt-3 pt-3 border-t border-gray-300">
            <p className="text-xs text-gray-500"><strong>Delivery Channels:</strong></p>
            <ul className="text-xs text-gray-500 space-y-1 mt-1">
              <li>• <strong>In-App:</strong> Notifications appear in your notification bell (top right)</li>
              <li>• <strong>Email:</strong> Notifications sent to your registered email address</li>
              <li>• <strong>SMS:</strong> Text message alerts to your phone (if configured)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Advanced Scoping Modal */}
      <NotificationScopeModal
        isOpen={showScopeModal}
        onClose={() => setShowScopeModal(false)}
        eventType={selectedEventType}
        studies={studies}
        sites={sites}
        onSave={handleScopedSubscription}
      />
    </div>
  );
};

export default NotificationSubscriptions;
